<script setup>

import AppLayout from "@/Layouts/AppLayout.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import {Link, useForm, usePage} from "@inertiajs/inertia-vue3";
import {computed, ref} from 'vue';

const props = defineProps({
  quote: Object
});

const page = usePage();
const quoteIssuer = computed(() => page.props.value.app?.quoteIssuer ?? null);

function openMailPreviewIfPresent() {
  const url = page.props.value?.quote_mail_preview_url
    ?? page.props.value?.flash?.quote_mail_preview_url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const currencyNumber = number => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    style: 'currency',
    currency: 'USD',
  }).format(number);
};

const companyImage = computed(() => {
  if (props.quote.company_id == 1) {
    return 'PDFSmall.jpg';
  } else if (props.quote.company_id == 2) {
    return 'Premier-logoSmall.jpg';
  } else if (props.quote.company_id == 3) {
    return 'login_logo.gif';
  }
});

const shipTo = computed(() => {
  if (props.quote.ship_to == 1) {
    return props.quote.ship_to_clientfield;
  } else if (props.quote.ship_to == 2) {
    return props.quote.ship_to_vendorfield;
  } else if (props.quote.ship_to == 3) {
    return `${props.quote.other_name}\n${props.quote.other_state}\n${props.quote.other_city}\n${props.quote.other_zip}\n${props.quote.other_street}`;
  }
});

const clientVendorOther = computed(() => {
  if (props.quote.ship_to == 1) {
    return 'Client';
  } else if (props.quote.ship_to == 2) {
    return 'Vendor';
  } else if (props.quote.ship_to == 3) {
    return 'Other';
  }
});

const formatDate = (timestamp, format) => {
  if (!timestamp) {
    return '';
  }
  return dayjs.tz(timestamp).format(format);
}

const print = () => {
  let contents = document.getElementById("content").innerHTML;
  const printWindow = window.open('', '', 'height=600,width=800');
  
  printWindow.document.write('<html><head><title>Print</title>');
  printWindow.document.write('<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">');
  // Print uses Tailwind 2 CDN (no whitespace-break-spaces). Force pre-wrap so description newlines survive print.
  printWindow.document.write('<style>.whitespace-pre-wrap{white-space:pre-wrap!important;}</style>');
  printWindow.document.write('</head><body>');
  printWindow.document.write(contents);
  printWindow.document.write('</body></html>');

  printWindow.document.close();
      
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
}

const sendingQuoteEmail = ref({quote: null, error: null, sending: false});
const sendingQuoteEmailForm = useForm({
  email: null,
  subject: 'Quote Request',
});
const openQuoteEmailModal = () => {
  sendingQuoteEmail.value.quote = props.quote;
}
const closeQuoteEmailModal = () => {
  sendingQuoteEmail.value = {quote: null, error: null, sending: false};
  sendingQuoteEmailForm.reset();
}

const sendQuoteEmail = () => {
  if (!sendingQuoteEmailForm.email) {
    sendingQuoteEmail.value.error = 'The recipient field is mandatory';
    return false;
  }
  if (!sendingQuoteEmailForm.subject) {
    sendingQuoteEmail.value.error = 'The subject field is mandatory';
    return false;
  }
  sendingQuoteEmail.value.sending = true;

  sendingQuoteEmailForm.post(route('admin.quotes.send-email', {quote: sendingQuoteEmail.value.quote.qid}), {
    preserveScroll: true,
    onSuccess: () => {
      sendingQuoteEmailForm.reset();
      closeQuoteEmailModal();
      openMailPreviewIfPresent();
    },
    onFinish: () => {
      sendingQuoteEmail.value.sending = false;
    },
  });
}

const resetQuoteEmailError = () => {
  sendingQuoteEmail.value.error = null;
}

const amountSubtotal = computed(() => {
  return props.quote.items?.reduce((n, item) => n + (item.amount ?? 0), 0) ?? 0;
});

const taxSubtotal = computed(() => {
  return props.quote.items?.reduce((n, item) => n + (item.tax_amount ?? 0), 0) ?? 0;
});

const total = computed(() => {
  return amountSubtotal.value + taxSubtotal.value;
});

</script>

<template>
  <AppLayout title="Quote">
    <template #header>
      <div class="flex justify-between">
        <div>
          <Link :href="route('admin.quotes.index')">
            <SecondaryButton>Back</SecondaryButton>
          </Link>
        </div>
        <div class="flex flex-col items-end space-y-2">
          <PrimaryButton type="button" @click.prevent="print">Print</PrimaryButton>
          <PrimaryButton type="button" @click.prevent="openQuoteEmailModal">Send Email</PrimaryButton>
        </div>
      </div>
    </template>


    <div class="mx-auto max-w-7xl py-10 px-24" id="content">
      <div class="grid grid-cols-2 gap-4">
        <div v-if="quoteIssuer?.show" class="flex flex-col mb-5">
          <!-- <div class="flex" style="max-height: 80px;">
            <img :src="`/content/images/${companyImage}`" alt="logo" class="h-full">
          </div> -->
          <b v-if="quoteIssuer.company_name">{{ quoteIssuer.company_name }}</b>
          <p v-if="quoteIssuer.address_line_1" class="leading-none">{{ quoteIssuer.address_line_1 }}</p>
          <p v-if="quoteIssuer.address_line_2" class="leading-none">{{ quoteIssuer.address_line_2 }}</p>
          <p v-if="quoteIssuer.country" class="leading-none">{{ quoteIssuer.country }}</p>
          <p v-if="quoteIssuer.voice" class="leading-none">{{ quoteIssuer.voice }}</p>
          <p v-if="quoteIssuer.fax" class="leading-none">{{ quoteIssuer.fax }}</p>
        </div>
        <div class="flex flex-col items-end">
          <b>QUOTE REQUEST</b>
          <p>Quote Number: {{ quote.po_number }}</p>
          <p>Internal Quote #: {{ quote.internal_po }}</p>
          <p>Quote Date: {{ formatDate(quote.po_date, 'MM/DD/YYYY') }}</p>
        </div>
        <div class="w-full">
          <table class="text-left text-sm font-light min-w-full">
            <thead>
              <tr>
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-t-lg">Quoted to</th>
              </tr>
            </thead>
            <tbody class="rounded-b-lg bg-gray-50">
              <tr>
                <td class="px-4 py-2 break-words whitespace-pre-wrap rounded-b-lg">{{shipTo}}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full">
          <table class="text-left text-sm font-light min-w-full">
            <thead>
              <tr>
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-t-lg">{{clientVendorOther}}</th>
              </tr>
            </thead>
            <tbody class="rounded-b-lg bg-gray-50">
              <tr>
                <td class="px-4 py-2 break-words whitespace-pre-wrap rounded-b-lg">{{quote.ship_to == 1 || quote.ship_to == 2 ? quote.client?.client : 'Other'}}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full col-span-2">
          <table class="text-left text-sm font-light min-w-full">
            <thead>
              <tr>
                <th v-if="quote.ship_to == 1" scope="col" class="px-4 py-2 bg-gray-100 rounded-tl-lg">Client ID</th>
                <th v-else-if="quote.ship_to == 2" scope="col" class="px-4 py-2 bg-gray-100">Vendor ID</th>
                <th scope="col" class="px-4 py-2 bg-gray-100">Good Thru</th>
                <th scope="col" class="px-4 py-2 bg-gray-100">Payment Terms</th>
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-tr-lg">Sales Rep</th>
              </tr>
            </thead>
            <tbody class="bg-gray-50">
              <tr>
                <td v-if="quote.ship_to == 1" class="px-4 py-2 break-words whitespace-pre-wrap rounded-bl-lg">{{ quote.ship_to_customer_id }}</td>
                <td v-if="quote.ship_to == 2" class="px-4 py-2 break-words whitespace-pre-wrap">{{ quote.shipto_vendor_id }}</td>
                <td class="px-4 py-2 break-words whitespace-pre-wrap">{{ formatDate(quote.good_thru, 'MM/DD/YYYY') }}</td>
                <td class="px-4 py-2 break-words whitespace-pre-wrap">{{ quote.payment_term?.payment }}</td>
                <td class="px-4 py-2 break-words whitespace-pre-wrap rounded-br-lg">{{ quote.sales_rep?.firstname }} {{ quote.sales_rep?.lastname }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full col-span-2">
          <table class="text-left text-sm font-light min-w-full">
            <thead>
              <tr>
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-tl-lg">Quantity</th>
                <th scope="col" class="px-2 py-2 bg-gray-100">Item</th>
                <th scope="col" class="px-2 py-2 bg-gray-100">Description</th>
                <th scope="col" class="px-4 py-2 bg-gray-100">Unit Price</th>
                <!-- <th scope="col" class="px-4 py-2 bg-gray-100">Tax Amount</th> -->
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-tr-lg whitespace-nowrap">Subtotal</th>
              </tr>
            </thead>
            <tbody v-if="quote.items.length > 0" class="bg-gray-50">
              <tr v-for="(item, index) in quote.items" :key="item.item_id ?? index">
                <td class="px-4 py-1 break-words whitespace-pre-wrap">{{ item.quantity }}</td>
                <td class="px-2 py-1 break-words whitespace-pre-wrap text-xs">{{ item.itemno }}</td>
                <td class="px-2 py-1 break-words whitespace-pre-wrap text-xs">{{ item.description }}</td>
                <td class="px-4 py-1 break-words whitespace-pre-wrap">{{ currencyNumber(item.unit_price) }}</td>
                <!-- <td class="px-4 py-1 break-words whitespace-pre-wrap">${{ item.tax_amount }}</td> -->
                <td class="px-4 py-1 break-words whitespace-pre-wrap">{{ currencyNumber(item.amount) }}</td>
              </tr>
            </tbody>
            <tbody v-else>
              <td class="px-4 py-2 break-words whitespace-pre-wrap rounded-b-lg">No items found</td>
            </tbody>
          </table>
        </div>
        <div class="w-full">
          <table class="text-left text-sm font-light min-w-full">
            <tbody>
            <tr class="rounded-t-lg">
              <th scope="col" class="px-4 py-1 bg-gray-100">Carrier</th>
              <td class="px-4 py-1 break-words bg-gray-50" v-text="quote.carrier?.carrier"></td>
            </tr>
            <tr class="">
              <th scope="col" class="px-4 py-1 bg-gray-100">Ship Via</th>
              <td class="px-4 py-1 break-words bg-gray-50" v-text="quote.ship_via?.shipvia"></td>
            </tr>
            <tr class="rounded-b-lg">
              <th scope="col" class="px-4 py-1 bg-gray-100">Shipper #</th>
              <td class="px-4 py-1 break-words bg-gray-50" v-text="quote.shipperno"></td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full">
          <table class="text-left text-sm font-light min-w-full">
            <tbody>
            <tr class="rounded-t-lg">
              <td class="px-4 py-1 bg-gray-50 whitespace-nowrap">Subtotal</td>
              <td class="px-4 py-1 break-words bg-gray-50">{{ currencyNumber(amountSubtotal) }}</td>
            </tr>
            <tr class="">
              <td class="px-4 py-1 bg-gray-50 whitespace-nowrap">Sales Tax</td>
              <td class="px-4 py-1 break-words bg-gray-50">{{ currencyNumber(taxSubtotal) }}</td>
            </tr>
            <tr class="rounded-b-lg">
              <th class="px-4 py-1 bg-gray-100 uppercase">Total</th>
              <th class="px-4 py-1 break-words bg-gray-100">{{ currencyNumber(total) }}</th>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full col-span-2">
          <table class="text-left text-sm font-light min-w-full">
            <thead>
              <tr>
                <th scope="col" class="px-4 py-2 bg-gray-100 rounded-t-lg">Instructions/Notes</th>
              </tr>
            </thead>
            <tbody class="rounded-b-lg bg-gray-50">
              <tr>
                <td class="px-4 py-2 break-words whitespace-pre-wrap rounded-b-lg text-xs">{{quote.instruction_notes}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <DialogModal :show="sendingQuoteEmail.quote !== null" @close="closeQuoteEmailModal">
      <template #title>
        Sending email for quote <b v-text="sendingQuoteEmail.quote.qid"></b>
      </template>

      <template #content>
        <p v-if="sendingQuoteEmail.error" v-text="sendingQuoteEmail.error" class="text-red-600"></p>
        <div class="mt-2">
          <InputLabel for="recipient" value="Recipient" :required="true"/>
          <TextInput
            id="recipient"
            type="email" :disabled="sendingQuoteEmail.sending"
            class="mt-1 w-full"
            placeholder="mail@example.com"
            v-model="sendingQuoteEmailForm.email"
            @input="resetQuoteEmailError"
          />
        </div>
        <div class="mt-2">
          <InputLabel for="subject" value="Subject" :required="true"/>
          <TextInput
            id="subject"
            type="text" :disabled="sendingQuoteEmail.sending"
            class="mt-1 w-full"
            placeholder="Quote Request"
            v-model="sendingQuoteEmailForm.subject"
            @input="resetQuoteEmailError"
          />
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeQuoteEmailModal" :disabled="sendingQuoteEmail.sending" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="sendQuoteEmail" :disabled="sendingQuoteEmail.sending">
          Send
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
